import React, { useState } from "react";
import { Card, DatePicker, Space, Button, Table, Typography, Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import {
  useInventoryReportsServiceGetProductSalesSummary,
  useInventoryReportsServiceDownloadProductSalesSummary,
} from "api/queries";
import dayjs from "dayjs";
const { Title } = Typography;

const SalesByProductReport = () => {
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [periodFilter, setPeriodFilter] = useState("This Month");

  const { data, isLoading } =
    useInventoryReportsServiceGetProductSalesSummary({
      startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    });

  const { mutate: getDownloadUrl, isPending } =
    useInventoryReportsServiceDownloadProductSalesSummary();

  const productSalesData =
    data?.productsSales?.map((item: any, idx: number) => ({
      key: item.productId || idx,
      productName: item.productName,
      categoryName: item.categoryName,
      subCategoryName: item.subCategoryName,
      brandName: item.brandName,
      sku: item.sku,
      quantitySold: item.quantitySold,
      revenue: item.totalRevenue,
      profitMargin: item.profitMargin,
    })) || [];

  const totals = data?.totals || {
    totalQuantitySold: 0,
    totalRevenue: 0,
    averageProfitMargin: 0,
  };

  const columns = [
    { title: "Product Name", dataIndex: "productName", key: "productName" },
    { title: "Category", dataIndex: "categoryName", key: "categoryName" },
    {
      title: "Subcategory",
      dataIndex: "subCategoryName",
      key: "subCategoryName",
    },
    { title: "Brand", dataIndex: "brandName", key: "brandName" },
    { title: "SKU", dataIndex: "sku", key: "sku" },
    { title: "Quantity Sold", dataIndex: "quantitySold", key: "quantitySold" },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: (value: number) => value?.toLocaleString(),
    },
    {
      title: "Profit Margin (%)",
      dataIndex: "profitMargin",
      key: "profitMargin",
      render: (value: number) => (value != null ? value.toFixed(2) : "0"),
    },
  ];

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
    }
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
  };

  const handleDownload = () => {
    getDownloadUrl(
      {
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: { success: boolean; message: string; downloadUrl?: string }) => {
          if (response?.downloadUrl) {
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute("download", "sales_by_product_report.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Title level={2}>Sales by Product Report</Title>
        <Space>
          <Select
            value={periodFilter}
            onChange={handlePeriodChange}
            style={{ width: 120 }}
          >
            <Select.Option value="Today">Today</Select.Option>
            <Select.Option value="This Week">This Week</Select.Option>
            <Select.Option value="This Month">This Month</Select.Option>
            <Select.Option value="This Year">This Year</Select.Option>
          </Select>
          <DatePicker
            value={startDate}
            onChange={handleStartDateChange}
            style={{ width: 120 }}
            placeholder="From"
          />
          <DatePicker
            value={endDate}
            onChange={handleEndDateChange}
            style={{ width: 120 }}
            placeholder="To"
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={isPending}
            onClick={handleDownload}
          >
            Download Report
          </Button>
        </Space>
      </Space>

      <Table
        columns={columns}
        dataSource={productSalesData}
        pagination={{ pageSize: 10 }}
        loading={isLoading}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5} align="right">
              <b>Totals</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5}>
              <b>{totals.totalQuantitySold}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6}>
              <b>{totals.totalRevenue?.toLocaleString()}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={7}>
              <b>{totals.averageProfitMargin?.toFixed(2)}</b>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </Space>
  );
};

export default SalesByProductReport;
